import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAttendanceCalendar } from "../../services/hrms.service";

const ManagerAttendance = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [attendanceCalendar, setAttendanceCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    leave: 0,
    holiday: 0,
  });

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const loadAttendanceCalendar = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      // Replace with actual API call
      // const response = await getAttendanceCalendar(user.employeeRef, month, year);
      
      // Dummy data for now
      const daysInMonth = new Date(year, month, 0).getDate();
      const calendarData = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();
        
        // Dummy logic - replace with actual API data
        let status = "present";
        let checkIn = "09:15 AM";
        let checkOut = "06:30 PM";
        
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          status = "holiday";
          checkIn = "-";
          checkOut = "-";
        } else if (day % 10 === 0) {
          status = "leave";
          checkIn = "-";
          checkOut = "-";
        } else if (day % 15 === 0) {
          status = "absent";
          checkIn = "-";
          checkOut = "-";
        }
        
        calendarData.push({
          date: day,
          status: status,
          fullDate: date,
          checkIn: checkIn,
          checkOut: checkOut,
          hours: status === "present" ? "9.25" : "0",
        });
      }
      
      setAttendanceCalendar(calendarData);
      
      // Calculate stats
      const presentDays = calendarData.filter(d => d.status === "present").length;
      const absentDays = calendarData.filter(d => d.status === "absent").length;
      const leaveDays = calendarData.filter(d => d.status === "leave").length;
      const holidayDays = calendarData.filter(d => d.status === "holiday").length;
      
      setStats({
        present: presentDays,
        absent: absentDays,
        leave: leaveDays,
        holiday: holidayDays,
      });
    } catch (error) {
      console.error("Error loading attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendanceCalendar();
  }, [currentMonth]);

  const getCalendarDayClass = (status) => {
    const classes = {
      present: "bg-green-100 text-green-800 border-green-300 hover:bg-green-200",
      absent: "bg-red-100 text-red-800 border-red-300 hover:bg-red-200",
      leave: "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200",
      holiday: "bg-gray-100 text-gray-600 border-gray-300",
    };
    return classes[status] || "bg-white";
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/hrms/manager/my-info")}
          className="flex items-center text-indigo-600 hover:text-indigo-700 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to My Info
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">My Attendance</h1>
          <p className="text-gray-600">View your attendance history and details</p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Present Days</p>
                <p className="text-3xl font-bold text-green-600">{stats.present}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Absent Days</p>
                <p className="text-3xl font-bold text-red-600">{stats.absent}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Leave Days</p>
                <p className="text-3xl font-bold text-blue-600">{stats.leave}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Holidays</p>
                <p className="text-3xl font-bold text-gray-600">{stats.holiday}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center space-x-6 mb-6 pb-6 border-b">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Present</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Absent</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Leave</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Holiday</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day Headers */}
            {dayNames.map(day => (
              <div key={day} className="text-center font-semibold text-gray-700 py-3">
                {day}
              </div>
            ))}

            {/* Empty cells for days before month starts */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square"></div>
            ))}

            {/* Calendar Days */}
            {attendanceCalendar.map((day) => (
              <div
                key={day.date}
                className={`aspect-square flex flex-col items-center justify-center rounded-lg border-2 font-semibold cursor-pointer transition ${getCalendarDayClass(
                  day.status
                )}`}
                title={`${day.status.toUpperCase()}\nCheck In: ${day.checkIn}\nCheck Out: ${day.checkOut}\nHours: ${day.hours}h`}
              >
                <span className="text-lg">{day.date}</span>
                {day.status === "present" && (
                  <span className="text-xs mt-1">{day.hours}h</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Attendance Details */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Attendance Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Check In</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Check Out</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Working Hours</th>
                </tr>
              </thead>
              <tbody>
                {attendanceCalendar
                  .filter(d => d.status !== "holiday")  
                  .slice(-10)
                  .reverse()
                  .map((day) => (
                    <tr key={day.date} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800">
                        {day.fullDate.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            day.status === "present"
                              ? "bg-green-100 text-green-700"
                              : day.status === "absent"
                              ? "bg-red-100 text-red-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {day.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800">{day.checkIn}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{day.checkOut}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-800">{day.hours}h</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerAttendance;